/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/** @jsxImportSource solid-js */

import {
  JSXElement,
  Component,
  createSignal,
  createEffect,
  onCleanup,
} from 'solid-js';
import { noShadowDOM } from 'solid-element';
export interface AuthImageProps {
  src: string;
  className?: string;
  errImg?: string;
  onClick?(e: MouseEvent): void;
  asBackground?: boolean;
  alt?: string;
  style?: Record<string, string | number>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  imageProps?: Record<string, any>;
  // Authentication token for image loading
  accessToken?: string;
  // Utility functions for library reusability
  updateForFeatureBranch?(url: string): string;
}

const AuthImage: Component<AuthImageProps> = (props): JSXElement => {
  noShadowDOM();
  let imgRef: HTMLImageElement | HTMLDivElement | undefined;
  let mounted = true;

  const [loading, setLoading] = createSignal<boolean>(true);
  const [error, setError] = createSignal<boolean>(false);
  const [data, setData] = createSignal<string>('');

  // Reactive getters for props
  const getSrc = () => props.src || '';
  const getClassName = () => props.className || '';
  const getErrImg = () => {
    const defaultImg =
      'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNNTAgMTBIMTBWNTBINTBWMTBaIiBzdHJva2U9IiNjY2MiIHN0cm9rZS13aWR0aD0iMiIgZmlsbD0iI2Y5ZjlmOSIvPjx0ZXh0IHg9IjMwIiB5PSIzNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEyIiBmaWxsPSIjNjY2Ij5JbWFnZTwvdGV4dD48L3N2Zz4=';
    if (props.updateForFeatureBranch && props.errImg) {
      return props.updateForFeatureBranch(props.errImg);
    }
    return props.errImg || defaultImg;
  };

  // Internal buildAuthImg function that uses accessToken
  const buildAuthImg = async (url: string): Promise<string> => {
    try {
      // Get access token from props
      const authToken = props.accessToken ? `Bearer ${props.accessToken}` : '';

      const response = await fetch(url, {
        method: 'GET',
        headers: authToken ? { Authorization: authToken } : {},
      });

      if (!response.ok) {
        throw new Error('Failed to fetch authenticated image');
      }

      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result as string);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('buildAuthImg error:', error);
      throw error;
    }
  };

  // Effect to handle image loading when src changes
  createEffect(() => {
    const src = getSrc();
    if (!src) return;

    setLoading(true);
    setError(false);

    if (src.includes('https://resources')) {
      setLoading(false);
      setData(src);
    } else {
      buildAuthImg(src)
        .then((base64Img) => {
          if (mounted) {
            setLoading(false);
            setData(base64Img);
            setError(false);
          }
        })
        .catch(() => {
          if (mounted) {
            setLoading(false);
            setError(true);
          }
        });
    }
  });

  // Cleanup function
  onCleanup(() => {
    mounted = false;
  });

  const handleClick = (event: MouseEvent) => {
    // Emit custom event for React wrapper
    if (imgRef) {
      const customEvent = new CustomEvent('auth-image-click', {
        detail: { originalEvent: event },
        bubbles: true,
      });
      imgRef.dispatchEvent(customEvent);
    }

    // Call onClick if provided
    if (props.onClick) {
      props.onClick(event);
    }
  };

  const imgClass = () => (loading() ? 'd-none' : getClassName());
  const imgSrc = () => (!error() ? data() : getErrImg());

  return (
    <>
      {loading() && !props.asBackground && (
        <i class="icon-font tc-icon-spinner-dark backgroundNoRepeat" />
      )}

      {!props.asBackground ? (
        <img
          ref={imgRef as HTMLImageElement}
          src={imgSrc()}
          class={`${imgClass()} mr-2`}
          onClick={handleClick}
          alt={props.alt}
          {...props.imageProps}
        />
      ) : (
        <div
          ref={imgRef as HTMLDivElement}
          class={imgClass()}
          onClick={handleClick}
          style={{
            'background-image': `url(${imgSrc()})`,
            ...props.style,
          }}
        />
      )}
    </>
  );
};

export default AuthImage;
